import { useCallback, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Volume2, VolumeX } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { muteVideoRef } from "@/lib/muteVideoRef";

interface TalkingAvatarHeroShowcaseProps {
  /** R2 URL of the source image (the user's selected preview). */
  beforeImage: string;
  /** R2 URL of the rendered lip-sync video. */
  afterVideo: string;
}

/**
 * Before/after showcase for the Talking Avatar hero. Same visual grammar as
 * Product Studio's `TransformationShowcase` — single source on the start side,
 * arrow in the middle, generated output on the end side — but the output is a
 * looping muted video instead of an image stack.
 *
 * The video autoplays muted+looped so the page can demo lip-sync without
 * waiting for user interaction. autoplay-with-sound is blocked by browsers
 * anyway; the muted preview hooks the user and they can tap to play with
 * sound after uploading their own photo.
 */
export function TalkingAvatarHeroShowcase({
  beforeImage,
  afterVideo,
}: TalkingAvatarHeroShowcaseProps) {
  const { t, isRTL } = useLanguage();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  // Start muted so autoplay works (browsers block sound-on-autoplay). User
  // taps the speaker icon to hear the lip-sync — that user gesture satisfies
  // the autoplay policy.
  const [muted, setMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // STABLE ref callback — runs only on mount/unmount, NOT on every render.
  // An inline `(el) => ...` ref re-runs on each render and would re-mute the
  // video right after the user taps unmute (setMuted triggers a re-render),
  // making the sound button need a second click.
  const attachVideo = useCallback((el: HTMLVideoElement | null) => {
    videoRef.current = el;
    muteVideoRef(el);
  }, []);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    // This is the explicit user gesture that opts this video out of the global
    // mute guard (index.html) — otherwise it would re-mute on unmute.
    v.dataset.allowSound = "1";
    v.muted = !v.muted;
    setMuted(v.muted);
    // First unmute often needs a play() nudge — most browsers pause the
    // video when the muted state flips on autoplay-only sources.
    if (!v.muted && v.paused) v.play().catch(() => undefined);
  };

  return (
    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 md:gap-4">
      {/* BEFORE — source photo */}
      <div className="relative aspect-[3/4] rounded-2xl overflow-hidden border border-border bg-card">
        <img
          src={beforeImage}
          alt={t.talkingAvatar.beforeLabel}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute top-2 md:top-2.5 start-2 md:start-2.5">
          <span className="inline-flex items-center px-2 py-0.5 md:px-2.5 md:py-1 rounded-md bg-black/75 backdrop-blur-sm text-[10px] md:text-xs font-bold text-white uppercase tracking-wider shadow-sm ring-1 ring-white/10">
            {t.talkingAvatar.beforeLabel}
          </span>
        </div>
      </div>

      <div className="aspect-[3/4] flex items-center shrink-0">
        <div className="w-10 h-10 rounded-full bg-background border border-border shadow-md flex items-center justify-center">
          <Arrow className="w-4 h-4 text-brand" />
        </div>
      </div>

      {/* AFTER — looping lip-sync video, starts muted for autoplay; user can
          tap the speaker icon to hear the rendered TTS audio. */}
      <div className="relative aspect-[3/4] rounded-2xl overflow-hidden border border-border bg-card">
        <video
          ref={attachVideo}
          src={afterVideo}
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute top-2 md:top-2.5 start-2 md:start-2.5">
          <span className="inline-flex items-center px-2 py-0.5 md:px-2.5 md:py-1 rounded-md bg-primary text-[10px] md:text-xs font-bold text-white uppercase tracking-wider shadow-sm">
            {t.talkingAvatar.afterLabel}
          </span>
        </div>
        <button
          type="button"
          onClick={toggleMute}
          aria-label={muted ? t.talkingAvatar.heroUnmuteAria : t.talkingAvatar.heroMuteAria}
          title={muted ? t.talkingAvatar.heroUnmuteAria : t.talkingAvatar.heroMuteAria}
          className="absolute bottom-2 end-2 md:bottom-3 md:end-3 w-9 h-9 md:w-10 md:h-10 rounded-full bg-black/70 hover:bg-black/85 backdrop-blur-sm text-white flex items-center justify-center shadow-md transition-colors"
        >
          {muted ? <VolumeX className="w-4 h-4 md:w-5 md:h-5" /> : <Volume2 className="w-4 h-4 md:w-5 md:h-5" />}
        </button>
      </div>
    </div>
  );
}
